import { useEffect, useState } from "react";
import queryString from "query-string";
import { useDebounce } from "use-debounce";
import { fetchWithAuth } from "../../../../api-client/api_client";
import { Integration } from "../../../../models/Entities";
import {
  ResolvedBlueprintIdentifier,
  isCompleteUUID,
  resolveBlueprintIdentifier,
} from "../utils/resolveBlueprintIdentifier";

// A search is aimed at one Integration, so the first page of matches is enough.
const SEARCH_RESULT_LIMIT = 20;

const useIntegrationSearch = () => {
  // state
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<Integration[]>();
  const [resolved, setResolved] = useState<ResolvedBlueprintIdentifier>();
  const [isNotFound, setIsNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // hooks
  const [debouncedSearchTerm] = useDebounce(searchTerm, 250);

  // derived state
  const isIdentifierSearch = isCompleteUUID(debouncedSearchTerm);

  // effects
  // A complete UUID is a Blueprint or BlueprintVersion ID, so it resolves to one Blueprint rather
  // than running the name search. Anything else searches Integration names as before.
  useEffect(() => {
    setResolved(undefined);
    setIsNotFound(false);

    if (!debouncedSearchTerm.trim()) {
      setResults(undefined);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    setIsLoading(true);

    if (isIdentifierSearch) {
      setResults(undefined);

      resolveBlueprintIdentifier(debouncedSearchTerm).then((match) => {
        if (isCancelled) return;
        setResolved(match ?? undefined);
        setIsNotFound(!match);
        setIsLoading(false);
      });

      return () => {
        isCancelled = true;
      };
    }

    const params = queryString.stringify({
      search_term: debouncedSearchTerm,
      order_by: "name",
      limit: SEARCH_RESULT_LIMIT,
      offset: 0,
    });

    fetchWithAuth({
      path: `/integrations/admin-meta?${params}`,
      method: "GET",
      onResponse: (data?: { results?: Integration[] }) => {
        if (isCancelled) return;
        setResults(data?.results ?? []);
        setIsLoading(false);
      },
      onError: () => {
        if (isCancelled) return;
        setResults([]);
        setIsLoading(false);
      },
    });

    return () => {
      isCancelled = true;
    };
  }, [debouncedSearchTerm, isIdentifierSearch]);

  return {
    searchTerm,
    setSearchTerm,
    results,
    isLoading,
    resolved,
    isNotFound,
    // Whether the current term looks like a Blueprint identifier, independent of whether the
    // resolve request has finished -- so the loading UI can match the final result's shape
    // instead of showing the name-search grid until resolution flips it over.
    isIdentifierSearch,
  };
};

export { useIntegrationSearch };
